import { Box, Button, Container, FormControl, IconButton, InputAdornment, OutlinedInput, TextField, Typography, Alert } from "@mui/material";
import React, { ChangeEvent, useEffect, useCallback } from "react";

import { Key, Person, Visibility, VisibilityOff, WifiOff } from "@mui/icons-material";
import { authApi, ErrorCodes, login } from "services/api";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useAuth } from "store/AuthProvider";
import useLoginController from "hooks/useLoginController";
import { getEventId, EventStatusError, EventStatus } from "utils/getEventId";
import { getPlatformImages } from "utils/getPlatformImages";
import { getBrand, isLumio } from "utils/platformStyle";
import { EventModal } from "./component/EventModal";

const SignIn = () => {
	const navigate = useNavigate();
	const auth = useAuth();

	const [data, setData] = React.useState({ username: "", password: "" });
	const [eventId, setEventId] = React.useState("");
	const [error, setError] = React.useState<{ [k: string]: boolean }>({});
	const [showPassword, setShowPassword] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [cachedUser, setCachedUser] = React.useState<{ id: string; name: string } | null>(null);
	const [dialogOpen, setDialogOpen] = React.useState(false);
	const [eventStatus, setEventStatus] = React.useState<EventStatus | null>(null);
	const [eventCheckCompleted, setEventCheckCompleted] = React.useState(false);

	const { handleLogin, handleResumeOfflineSession, isOnline } = useLoginController();

	// Define handleResumeSession early with useCallback
	const handleResumeSession = useCallback(async () => {
		if (isSubmitting || !cachedUser) return;

		setIsSubmitting(true);
		try {
			const success = await handleResumeOfflineSession(cachedUser);
			// Navigation is handled by useEffect when isAuth changes
			if (!success) {
				setIsSubmitting(false);
			}
		} catch (error) {
			console.error("Resume session failed:", error);
			enqueueSnackbar("恢復會話過程中發生未預期錯誤", { variant: "error" });
			setIsSubmitting(false);
		}
	}, [isSubmitting, cachedUser, handleResumeOfflineSession]);

	// Auto-navigate when user becomes authenticated
	// This handles both: 1) New login/resume session, 2) Page load with existing auth
	useEffect(() => {
		if (auth?.isAuth) {
			navigate("/", { replace: true });
		}
	}, [auth?.isAuth, navigate]);

	// Initialize cached user and eventId on mount
	useEffect(() => {
		const cachedUserData = auth?.getLastCachedUser();
		setCachedUser(cachedUserData);
		
		// If user is already authenticated (e.g., from localStorage on app startup),
		// the auto-navigate useEffect above will redirect them to home page
		// No need to do anything here, just let the navigation happen
	}, [auth]);

	// Check eventId only when needed (not already authenticated)
	React.useEffect(() => {
		// Skip eventId check if user is already authenticated
		// They will be redirected to home page by the auto-navigate effect
		if (auth?.isAuth) {
			return;
		}

		const checkEventId = async () => {
			// Skip event ID check if offline and has cached user
			// (offline session will use cached eventId)
			if (!isOnline && cachedUser) {
				const cachedUserData = auth?.getLastCachedUser();
				if (cachedUserData?.eventId) {
					setEventId(cachedUserData.eventId);
					setEventCheckCompleted(true);
					return;
				}
			}
			
			try {
				const eventId = await getEventId();
				setEventId(eventId);
				setEventCheckCompleted(true);
			} catch (error) {
				// Only show dialog if not in offline mode with cached user
				if (isOnline || !cachedUser) {
					if (error instanceof EventStatusError) {
						setEventStatus(error.status);
					} else {
						setEventStatus("not_start");
					}
					setDialogOpen(true);
					setEventCheckCompleted(true);
				} else {
					setEventCheckCompleted(true);
				}
			}
		};
		checkEventId();
	}, [auth?.isAuth, isOnline, cachedUser, auth])

	const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			handleSubmit();
		}
	};

	const handleClickShowPassword = () => setShowPassword((show) => !show);
	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setData({ ...data, [name]: value });
	};
	// const handleSubmit = () => {
	// 	if (isSubmitting) return;
	// 	if (!data.username || !data.password) {
	// 		setError({ username: !data.username, password: !data.password });
	// 		enqueueSnackbar("請輸入登入名稱及登入密碼", { variant: "error" });
	// 		return;
	// 	}
	// 	setIsSubmitting(true);
	// 	login(data.username, data.password)
	// 		.then((resp) => {
	// 			auth.login(resp);
	// 			navigate("/", { replace: true });
	// 			setIsSubmitting(false);
	// 		})
	// 		.catch((error) => {
	// 			switch (error.errorCode) {
	// 				case ErrorCodes.PWD_INCORRECT:
	// 					enqueueSnackbar("登入密碼不正確", { variant: "error" });
	// 					break;
	// 				case ErrorCodes.USER_NOT_FOUND:
	// 					enqueueSnackbar("登入名稱不存在", { variant: "error" });
	// 					break;
	// 				default:
	// 					enqueueSnackbar("登入失敗", { variant: "error" });
	// 			}
	// 			setIsSubmitting(false);
	// 		});
	// };

	const handleSubmit = async () => {
		// Block login if event has not started or has ended
		if (eventStatus === "not_start" || eventStatus === "end") {
			enqueueSnackbar(eventStatus === "end" ? "活動已結束,無法登入" : "活動未開始,請在活動開始後再登入", { variant: "warning" });
			return;
		}

		if (isSubmitting) return;
		if (!data.username || !data.password) {
			setError({ username: !data.username, password: !data.password });
			enqueueSnackbar("請輸入登入名稱及登入密碼", { variant: "error" });
			return;
		}
		setIsSubmitting(true);
		try {
			const success = await handleLogin(eventId, data.username, data.password);
			// Navigation is handled by useEffect when isAuth changes
			if (!success) {
				// Only re-enable form if login failed
				setIsSubmitting(false);
			}
		} catch (error) {
			console.error("Login failed:", error);
			enqueueSnackbar("登入過程中發生未預期錯誤", { variant: "error" });
			setIsSubmitting(false);
		}
	}

	const BRAND = getBrand();
	const { bg: BackgroundImage, logo: LogoImage } = getPlatformImages(BRAND);
	return (
		<Container sx={{ mt: 6, pt: "18%", px: 2.5, display: "flex", flexDirection: "column", alignItems: "center", height: 1 }}>
			<Box
				position={"absolute"}
				width={1}
				zIndex={-1}
				left={0}
				bottom={0}
				overflow={"hidden"}
				sx={{ inset: 0 }}>
				<Box
					component={"img"}
					src={BackgroundImage}
					width={1}
					height={1}
					sx={{ objectFit: "cover", objectPosition: "bottom" }}
				/>
			</Box>
			<Box
				component={"img"}
				src={LogoImage}
				width={200}
				mb={4}
			/>

			{/* A3: Offline Mode - No Cached Session */}
			{!isOnline && !cachedUser && (
				<Alert
					severity="error"
					icon={<WifiOff />}
					sx={{
						width: '100%',
						mb: 3,
						backgroundColor: 'rgba(211, 47, 47, 0.1)',
						'& .MuiAlert-icon': {
							color: 'error.main'
						}
					}}
				>
					<Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
						無法登入
					</Typography>
					<Typography variant="body2">
						無互聯網連接無法登錄。此設備沒有可用的緩存數據。
					</Typography>
				</Alert>
			)}

			{/* A4: Offline Mode - Cached Session Available */}
			{!isOnline && cachedUser ? (
				<Alert
					severity="info"
					icon={<WifiOff />}
					sx={{
						width: '100%',
						mb: 3,
						backgroundColor: 'rgba(2, 136, 209, 0.1)',
						'& .MuiAlert-icon': {
							color: 'info.main'
						}
					}}
				>
					<Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
						未檢測到互聯網連接
					</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						點擊下方按鈕恢復 <strong>{cachedUser.name}</strong> 的緩存數據會話
					</Typography>
					<Button
						fullWidth
						variant="contained"
						color="info"
						disabled={isSubmitting}
						onClick={handleResumeSession}
						sx={{
							textTransform: 'none',
							fontWeight: 500
						}}
					>
						恢復 {cachedUser.name} 的會話
					</Button>
				</Alert>
			) : (
				<>
			{/* A1 & A2 & A3: Login Form (disabled in A3) */}
			<FormControl
				fullWidth
				error={error?.username}
				margin="dense"
				variant="outlined">
				{/* <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel> */}
				<OutlinedInput
					name="username"
					value={data.username}
					onChange={handleChange}
					onKeyDown={handleEnter}
					placeholder="登入名稱"
					startAdornment={
						<InputAdornment position="start">
							<Person
								color="primary"
								sx={isLumio() ? { color: "#007bff" } : undefined}
							/>
						</InputAdornment>
					}
					sx={{ backgroundColor: "common.white" }}
				/>
			</FormControl>

			<FormControl
				fullWidth
				error={error?.password}
				margin="dense"
				variant="outlined"
				sx={{ mb: 4 }}>
				{/* <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel> */}
				<OutlinedInput
					id="outlined-adornment-password"
					name="password"
					value={data.password}
					onChange={handleChange}
					onKeyDown={handleEnter}
					type={showPassword ? "text" : "password"}
					placeholder="登入密碼"
					startAdornment={
						<InputAdornment position="start">
							<Key
								color="primary"
								sx={isLumio() ? { color: "#007bff" } : undefined}
							/>
						</InputAdornment>
					}
					endAdornment={
						<InputAdornment position="end">
							<IconButton
								aria-label={showPassword ? "hide the password" : "display the password"}
								onClick={handleClickShowPassword}
								edge="end">
								{showPassword ? <Visibility /> : <VisibilityOff />}
							</IconButton>
						</InputAdornment>
					}
					sx={{ backgroundColor: "common.white" }}
				/>
			</FormControl>

			<Button
				fullWidth
				variant="contained"
				color="primary"
				disabled={isSubmitting || dialogOpen || eventStatus === "not_start" || eventStatus === "end"}
				onClick={handleSubmit}>
				登入
			</Button>
			</>
			)}
			<EventModal 
				open={dialogOpen} 
				status={eventStatus === "end" ? "end" : "not_start"}
				onClose={() => setDialogOpen(false)}
			/>
		</Container>
	);
};

export default SignIn;
